export interface AppMessage {
  title: string;
  message: string;
}

export interface AppConfirmMessage extends AppMessage {
  confirmText: string;
  cancelText: string;
}

export interface AppTypedConfirmMessage extends AppConfirmMessage {
  expectedText: string;
}

export const APP_MESSAGES = {
  COMMON: {
    TITLES: {
      SUCCESS: 'Success',
      ERROR: 'Unable to Complete Request',
      WARNING: 'Warning',
      INFORMATION: 'Information',
      VALIDATION: 'Please Check the Form',
      CONFIRMATION: 'Please Confirm'
    },

    BUTTONS: {
      OK: 'OK',
      CLOSE: 'Close',
      CANCEL: 'Cancel',
      RETRY: 'Retry',
      CONFIRM: 'Confirm',
      YES_CONFIRM: 'Yes, I Confirm'
    },

    ERRORS: {
      DEFAULT: "We're having trouble completing your request right now. Please try again in a moment.",
      NETWORK: 'The service is currently unavailable. Please check your connection and try again.',
      UNAUTHORIZED: 'Your session is not valid. Please sign in again.',
      FORBIDDEN: 'You do not have permission to perform this action.',
      NOT_FOUND: 'The requested resource was not found.',
      SERVER: 'Our servers are having an issue right now. Please try again in a few minutes.'
    }
  },

  APP: {
    DEFAULT_PAGE_TITLE: 'Dashboard',
    DEFAULT_USER_LABEL: 'User',
    FALLBACK_PAGE_LABEL: 'this page',

    ACCESS_DENIED: {
      title: 'Access Denied',
      message: (pageName: string) =>
        `You do not have permission to access ${pageName}. You have been redirected to an available page.`
    },

    SESSION_TIMEOUT: {
      title: 'Session Timed Out',
      message: 'You have not been active for the last 6 hours. For your security, you will be logged out.'
    }
  },

  CANDIDATES: {
    VALIDATION: {
      SKILLS_OR_PROFILE_REQUIRED: {
        title: 'Profile or Skill Required',
        message: 'Please provide either a profile document or at least one skill for the candidate.'
      }
    }
  },

  USERS: {
    VIEW: {
      MISSING_ID: {
        title: 'Unable to Open User',
        message: 'User id is missing.'
      },

      MISSING_DETAILS: {
        title: 'Unable to Load User',
        message: 'User details are missing.'
      },

      LOAD_FAILED: {
        title: 'Unable to Load User',
        message: 'Failed to load user details.'
      },

      EDIT_MISSING_ID: {
        title: 'Unable to Edit User',
        message: 'User id is missing.'
      }
    },

    FORM: {
      LOAD_ROLES_FAILED: {
        title: 'Unable to Load Roles',
        message: 'Failed to load available roles.'
      },

      LOAD_USER_FAILED: {
        title: 'Unable to Load User',
        message: 'Failed to load user details.'
      },

      USER_DETAILS_MISSING: {
        title: 'Unable to Load User',
        message: 'User details are missing.'
      },

      LOAD_MANAGERS_FAILED: {
        title: 'Unable to Load Parent Users',
        message: 'Failed to load eligible parent users.'
      },

      MANAGER_DETAILS_MISSING: {
        title: 'Unable to Load Parent Users',
        message: 'Parent user details are missing.'
      },

      INVALID_FORM: {
        title: 'Please Check the Form',
        message: 'Complete all required fields before saving the user.'
      },

      CREATE_FAILED: {
        title: 'Unable to Create User',
        message: 'Failed to create user.'
      },

      UPDATE_FAILED: {
        title: 'Unable to Update User',
        message: 'Failed to update user.'
      },

      CREATED: {
        title: 'User Created',
        message: 'User created successfully.'
      },

      UPDATED: {
        title: 'User Updated',
        message: 'User updated successfully.'
      },

      UI: {
        CREATE_TITLE: 'Create User',
        EDIT_TITLE: 'Edit User',
        LOADING: 'Loading user details...',
        USER_ALT: 'User',
        NAME: 'Name',
        NAME_PLACEHOLDER: 'Enter Full Name',
        NAME_REQUIRED: 'Name is required.',
        NAME_MIN: 'Name must contain at least 2 characters.',
        NAME_MAX: 'Name cannot exceed 255 characters.',
        NAME_PATTERN: 'Name contains unsupported characters.',
        EMAIL: 'Email',
        EMAIL_PLACEHOLDER: 'Enter Email',
        EMAIL_REQUIRED: 'Email is required.',
        EMAIL_MAX: 'Email cannot exceed 100 characters.',
        EMAIL_INVALID: 'Please enter a valid email.',
        PHONE: 'Phone Number',
        PHONE_PLACEHOLDER: 'Enter Phone Number',
        STATUS: 'Status',
        ACTIVE: 'Active',
        INACTIVE: 'Inactive',
        ASSIGN_ROLE: 'Assign Role',
        SELECT_ROLES: 'Select Roles',
        ROLE_REQUIRED: 'At least one role is required.',
        REPORTS_TO: 'Reports To',
        LOADING_MANAGERS: 'Loading eligible parent users...',
        SELECT_PARENT: 'Select Parent User',
        PARENT_REQUIRED: 'Parent user is required.',
        NO_PARENT_AVAILABLE: 'No eligible parent users are available.',
        AUTO_ASSIGNED: 'Automatically assigned',
        ASSIGNED_ROLE: 'Assigned Role',
        PASSWORD: 'Password',
        OPTIONAL: 'Optional',
        PASSWORD_PLACEHOLDER: 'Enter Password',
        PASSWORD_REQUIRED: 'Password is required.',
        PASSWORD_MIN: 'Password must contain at least 8 characters.',
        PASSWORD_MAX: 'Password cannot exceed 64 characters.',
        PASSWORD_PATTERN: 'Password must contain an uppercase letter, a number, and one allowed special character: @, $, #, !, %, &.',
        AUTO_GENERATE_PASSWORD: 'Auto Generate Password',
        RESET_FIRST_LOGIN: 'Reset Password on First Login',
        PROFILE_IMAGE: 'Profile Image',
        ADDRESS: 'Address',
        ADDRESS_PLACEHOLDER: 'Enter Address',
        ADDRESS_MAX: 'Address cannot exceed 2000 characters.',
        CANCEL: 'Cancel',
        SAVE: 'Save',
        SAVING: 'Saving...'
      },

      PHONE_VALIDATION: {
        REQUIRED: 'Phone number is required.',
        INDIA_EXACT: 'Enter exactly 10 digits for India.',
        DEFAULT_COUNTRY: 'the selected country',
        INVALID_FOR_COUNTRY: (countryName: string) =>
          `Enter a valid phone number for ${countryName}.`
      }
    },

    LIST: {
      LOAD_FAILED: {
        title: 'Unable to Load Users',
        message: 'Failed to load users.'
      },

      RETRY_LOAD: {
        confirmText: 'Retry',
        cancelText: 'Close'
      }
    },

    STATUS: {
      ACTIVATE_CONFIRM: {
        title: 'Activate User',
        message: 'Are you sure you want to activate this user account?',
        confirmText: 'OK',
        cancelText: 'Cancel'
      },

      DEACTIVATE_CONFIRM: {
        title: 'Deactivate User',
        message: 'Are you sure you want to deactivate this user account?',
        confirmText: 'OK',
        cancelText: 'Cancel'
      },

      ACTIVATED: {
        title: 'User Activated',
        message: 'User activated successfully.'
      },

      DEACTIVATED: {
        title: 'User Deactivated',
        message: 'User deactivated successfully.'
      },

      UPDATE_FAILED: {
        title: 'Unable to Update User',
        message: 'Failed to update user status.'
      }
    },

    DELETE: {
      CONFIRM: {
        title: 'Delete User',
        message: 'This user will be permanently deleted. This action cannot be undone.',
        expectedText: 'DELETE',
        confirmText: 'Yes, I Confirm',
        cancelText: 'Cancel'
      },

      DELETE_ALL_CONFIRM: {
        title: 'Delete All Users',
        message: 'All selected users will be permanently deleted. This action cannot be undone.',
        expectedText: 'DELETE ALL',
        confirmText: 'Yes, I Confirm',
        cancelText: 'Cancel'
      },

      SUCCESS: {
        title: 'User Deleted',
        message: 'User deleted successfully.'
      },

      FAILED: {
        title: 'Unable to Delete User',
        message: 'Failed to delete user.'
      }
    },

    PASSWORD_RESET: {
      MISSING_USER_ID: {
        title: 'Unable to Reset Password',
        message: 'User id is missing.'
      },

      INVALID_FORM: {
        title: 'Please Check the Form',
        message: 'Enter a valid new password and confirm that both passwords match.'
      },

      SUCCESS: {
        title: 'Password Reset',
        message: 'User password reset successfully.'
      },

      FAILED: {
        title: 'Unable to Reset Password',
        message: 'Unable to reset user password.'
      },

      ACCESS_DENIED: {
        title: 'Access Restricted',
        message: 'Only administrators can reset user passwords.'
      }
    },

    CHANGE_PASSWORD: {
      INVALID_FORM: {
        title: 'Please Check the Form',
        message: 'Enter your current password, a valid new password, and matching confirmation.'
      },

      SUCCESS: {
        title: 'Password Updated',
        message: 'Password updated successfully.'
      },

      FAILED: {
        title: 'Unable to Update Password',
        message: 'Unable to update password.'
      }
    },

    PROFILE: {
      LOAD_FAILED: {
        title: 'Unable to Load Profile',
        message: 'Unable to load profile details.'
      },

      INVALID_FORM: {
        title: 'Please Check the Form',
        message: 'Complete all required fields before saving your profile.'
      },

      PHONE_REQUIRED: {
        title: 'Phone Number Required',
        message: 'Enter a valid phone number before saving your profile.'
      },

      UPDATE_FAILED: {
        title: 'Unable to Update Profile',
        message: 'Unable to update profile.'
      },

      UPDATED: {
        title: 'Profile Updated',
        message: 'Profile updated successfully.'
      },

      IMAGE_TYPE_INVALID: {
        title: 'Invalid Image Type',
        message: 'Only JPG, PNG and WEBP images are allowed.'
      },

      IMAGE_SIZE_INVALID: {
        title: 'Image Too Large',
        message: 'Profile image must be 2 MB or smaller.'
      },

      IMAGE_READ_FAILED: {
        title: 'Unable to Read Image',
        message: 'Unable to read the selected image.'
      }
    },

    UNLOCK: {
      ACCESS_DENIED: {
        title: 'Access Restricted',
        message: 'Only administrators can unlock user accounts.'
      },

      ALREADY_UNLOCKED: {
        title: 'Account Already Unlocked',
        message: 'This user account is already unlocked.'
      },

      CONFIRM: {
        title: 'Unlock User Account',
        message: 'Are you sure you want to unlock this user account?',
        confirmText: 'Unlock',
        cancelText: 'Cancel'
      },

      SUCCESS: {
        title: 'Account Unlocked',
        message: 'Account unlocked successfully.'
      },

      FAILED: {
        title: 'Unable to Unlock Account',
        message: 'Unable to unlock user account.'
      }
    }
  },

  ROLES: {
    VIEW: {
      MISSING_ID: {
        title: 'Unable to Open Role',
        message: 'Role id is missing.'
      },
      MISSING_DETAILS: {
        title: 'Unable to Load Role',
        message: 'Role details are missing.'
      },
      LOAD_FAILED: {
        title: 'Unable to Load Role',
        message: 'Failed to load role details.'
      },
      PERMISSIONS_FAILED: {
        title: 'Unable to Load Permissions',
        message: 'Failed to load role permissions.'
      },
      ADMIN_EDIT_RESTRICTED: {
        title: 'Action Restricted',
        message: 'The Admin role cannot be edited.'
      }
    },

    FORM: {
      ROLE_DETAILS_MISSING: {
        title: 'Unable to Load Role',
        message: 'Role details are missing.'
      },

      LOAD_ROLE_FAILED: {
        title: 'Unable to Load Role',
        message: 'Failed to load role details.'
      },

      LOAD_TEMPLATE_FAILED: {
        title: 'Unable to Load Permissions',
        message: 'Failed to load the permission template.'
      },

      ADMIN_EDIT_RESTRICTED: {
        title: 'Action Restricted',
        message: 'The Admin role cannot be edited.'
      },

      INVALID_FORM: {
        title: 'Please Check the Form',
        message: 'Complete all required fields before saving the role.'
      },

      PERMISSION_REQUIRED: {
        title: 'Permission Required',
        message: 'Select at least one permission before saving the role.'
      },

      CREATE_FAILED: {
        title: 'Unable to Create Role',
        message: 'Failed to create role.'
      },

      UPDATE_FAILED: {
        title: 'Unable to Update Role',
        message: 'Failed to update role.'
      },

      CREATED: {
        title: 'Role Created',
        message: 'Role created successfully.'
      },

      UPDATED: {
        title: 'Role Updated',
        message: 'Role updated successfully.'
      },

      UI: {
        CREATE_TITLE: 'Create Role',
        EDIT_TITLE: 'Edit Role',
        LOADING: 'Loading role...',
        ROLE_ALT: 'Role',
        NAME: 'Name',
        NAME_PLACEHOLDER: 'Enter Name',
        NAME_REQUIRED: 'Name is required.',
        NAME_MIN: 'Name must contain at least 2 characters.',
        NAME_MAX: 'Name cannot exceed 150 characters.',
        HIERARCHY: 'Hierarchy Level',
        HIERARCHY_PLACEHOLDER: 'Hierarchy Level',
        HIERARCHY_HELP: (nextLevel: number | null | undefined) =>
          `Level 1 is reserved for System Admin. Existing hierarchy levels can be reused. The next new level is ${nextLevel ?? ''}.`,
        HIERARCHY_REQUIRED: 'Hierarchy level is required.',
        HIERARCHY_MIN: 'Hierarchy level 1 is reserved for System Admin. Please use level 2 or above.',
        HIERARCHY_MAX: (nextLevel: number | null | undefined) =>
          `Please select an existing hierarchy level or the next level ${nextLevel ?? ''}.`,
        STATUS: 'Status',
        ACTIVE: 'Active',
        INACTIVE: 'Inactive',
        DESCRIPTION: 'Description',
        DESCRIPTION_PLACEHOLDER: 'Description',
        DESCRIPTION_MAX: 'Description cannot exceed 1000 characters.',
        PERMISSIONS: 'Permissions',
        ALL_WITH_COLON: 'All :',
        ALL: 'All',
        SELECT_ALL: 'Select All',
        PERMISSION_REQUIRED_INLINE: 'At least one permission is required',
        CANCEL: 'Cancel',
        SAVE: 'Save'
      },

      HIERARCHY_LEVEL_ONE_WARNING: {
        title: 'Invalid Hierarchy Level',
        message: 'Hierarchy level 1 is reserved for System Admin. Please select hierarchy level 2 or above.'
      },

      HIERARCHY_GAP_WARNING: {
        title: 'Invalid Hierarchy Level',
        message: (level: number, currentMaxLevel: number | null | undefined, nextLevel: number) =>
          `Hierarchy level ${level} would skip hierarchy levels. Please select an existing level from 2 to ${currentMaxLevel ?? ''} or use the next level ${nextLevel}.`
      },

      MANDATORY_LIST_WARNING: {
        title: 'Mandatory Permission',
        message: 'List permission is mandatory and cannot be unchecked while other permissions are selected.'
      }
    },

    LIST: {
      LOAD_FAILED: {
        title: 'Unable to Load Roles',
        message: 'Failed to load roles.'
      }
    },

    STATUS: {
      ACTIVATE_CONFIRM: {
        title: 'Activate Role',
        message: 'Are you sure you want to activate this role?',
        confirmText: 'OK',
        cancelText: 'Cancel'
      },
      DEACTIVATE_CONFIRM: {
        title: 'Deactivate Role',
        message: 'Are you sure you want to deactivate this role?',
        confirmText: 'OK',
        cancelText: 'Cancel'
      },
      ACTIVATED: {
        title: 'Role Activated',
        message: 'Role activated successfully.'
      },
      DEACTIVATED: {
        title: 'Role Deactivated',
        message: 'Role deactivated successfully.'
      },
      UPDATE_FAILED: {
        title: 'Unable to Update Role',
        message: 'Failed to update role status.'
      }
    },

    DELETE: {
      ADMIN_RESTRICTED: {
        title: 'Action Restricted',
        message: 'The Admin role cannot be deleted.'
      },
      CONFIRM: {
        title: 'Delete Role',
        message: 'This role will be permanently deleted. This action cannot be undone.',
        expectedText: 'DELETE',
        confirmText: 'Yes, I Confirm',
        cancelText: 'Cancel'
      },
      SUCCESS: {
        title: 'Role Deleted',
        message: 'Role deleted successfully.'
      },
      FAILED: {
        title: 'Unable to Delete Role',
        message: 'Failed to delete role.'
      }
    }
  },

  CONFIGURATION_SETTINGS: {
    UI: {
      PAGE_TITLE: 'Configuration Settings',
      SEARCH_PLACEHOLDER: 'Search parent, sub group, name or value',
      FILTER: 'FILTER',
      FILTER_TOOLTIP: 'Filter',
      SAVE_TOOLTIP: 'Save changed values',
      SAVE_CHANGES: 'SAVE CHANGES',
      SAVING: 'SAVING...',
      LOADING: 'LOADING CONFIGURATION SETTINGS...',
      EMPTY_TITLE: 'NO CONFIGURATION SETTINGS FOUND',
      EMPTY_MESSAGE: 'CHANGE THE CURRENT SEARCH/FILTER.',
      PREVIOUS_VALUE: 'PREVIOUS VALUE',
      EMPTY_VALUE: 'EMPTY',
      RESTORE: 'RESTORE',
      RESTORE_TOOLTIP: 'Restore previous value',
      NEW_VALUE: 'NEW VALUE',
      VALUE: 'VALUE',
      VALUE_REQUIRED: 'PLEASE ENTER THE VALUE.',
      UNSAVED_CHANGE: 'UNSAVED CHANGE',
      FILTER_BY: 'FILTER BY',
      PARENT_GROUP: 'PARENT GROUP',
      SUB_GROUP: 'SUB GROUP',
      SELECT_PARENT_GROUP: 'Select Parent Group',
      SELECT_SUB_GROUP: 'Select Sub Group',
      CANCEL: 'CANCEL',
      CLEAR: 'CLEAR',
      APPLY: 'APPLY'
    },

    LOAD_FAILED: {
      title: 'Configuration Settings',
      message: 'Unable to load configuration settings.'
    },

    VALIDATION: {
      title: 'Validation',
      message: 'Please enter the value for all modified configuration fields.'
    },

    UPDATED: {
      title: 'Configuration Settings',
      message: (changedCount: number) =>
        `${changedCount} configuration value${changedCount === 1 ? '' : 's'} updated successfully.`
    },

    UPDATE_FAILED: {
      title: 'Configuration Settings',
      message: 'Unable to update configuration values.'
    }
  },

  MENU: {
    VIEW: {
      MISSING_ID: {
        title: 'Unable to Open Menu',
        message: 'Menu id is missing.'
      },
      MISSING_DETAILS: {
        title: 'Unable to Load Menu',
        message: 'Menu details are missing.'
      },
      LOAD_FAILED: {
        title: 'Unable to Load Menu',
        message: 'Failed to load menu details.'
      },
      EDIT_MISSING_ID: {
        title: 'Unable to Edit Menu',
        message: 'Menu id is missing.'
      }
    },

    FORM: {
      MENU_DETAILS_MISSING: {
        title: 'Unable to Load Menu',
        message: 'Menu details are missing.'
      },
      LOAD_MENU_FAILED: {
        title: 'Unable to Load Menu',
        message: 'Failed to load menu details.'
      },
      LOAD_PARENTS_FAILED: {
        title: 'Unable to Load Parent Menus',
        message: 'Failed to load parent menus.'
      },
      INVALID_FORM: {
        title: 'Please Check the Form',
        message: 'Complete all required fields before saving the menu.'
      },
      CREATE_FAILED: {
        title: 'Unable to Create Menu',
        message: 'Failed to create menu.'
      },
      UPDATE_FAILED: {
        title: 'Unable to Update Menu',
        message: 'Failed to update menu.'
      },
      CREATED: {
        title: 'Menu Created',
        message: 'Menu created successfully.'
      },
      UPDATED: {
        title: 'Menu Updated',
        message: 'Menu updated successfully.'
      }
    },

    LIST: {
      LOAD_FAILED: {
        title: 'Unable to Load Menus',
        message: 'Failed to load menus.'
      },
      LOAD_PARENTS_FAILED: {
        title: 'Unable to Load Parent Menus',
        message: 'Failed to load parent menu options.'
      },
      RETRY_LOAD: {
        confirmText: 'Retry',
        cancelText: 'Close'
      }
    },

    STATUS: {
      ACTIVATE_CONFIRM: {
        title: 'Activate Menu',
        message: 'Are you sure you want to activate this menu?',
        confirmText: 'OK',
        cancelText: 'Cancel'
      },
      DEACTIVATE_CONFIRM: {
        title: 'Deactivate Menu',
        message: 'Are you sure you want to deactivate this menu?',
        confirmText: 'OK',
        cancelText: 'Cancel'
      },
      ACTIVATED: {
        title: 'Menu Activated',
        message: 'Menu activated successfully.'
      },
      DEACTIVATED: {
        title: 'Menu Deactivated',
        message: 'Menu deactivated successfully.'
      },
      UPDATE_FAILED: {
        title: 'Unable to Update Menu',
        message: 'Failed to update menu status.'
      }
    },

    DELETE: {
      CONFIRM: {
        title: 'Delete Menu',
        message: 'This menu will be permanently deleted. This action cannot be undone.',
        expectedText: 'DELETE',
        confirmText: 'Yes, I Confirm',
        cancelText: 'Cancel'
      },
      SUCCESS: {
        title: 'Menu Deleted',
        message: 'Menu deleted successfully.'
      },
      FAILED: {
        title: 'Unable to Delete Menu',
        message: 'Failed to delete menu.'
      }
    },

    PERMISSIONS: {
      MISSING_MENU_ID: {
        title: 'Unable to Open Permissions',
        message: 'Menu id is missing.'
      },
      LOAD_FAILED: {
        title: 'Unable to Load Permissions',
        message: 'Failed to load menu permissions.'
      },
      INVALID_FORM: {
        title: 'Please Check the Form',
        message: 'Complete all required fields before saving the permission.'
      },
      CREATE_FAILED: {
        title: 'Unable to Create Permission',
        message: 'Failed to create permission.'
      },
      UPDATE_FAILED: {
        title: 'Unable to Update Permission',
        message: 'Failed to update permission.'
      },
      CREATED: {
        title: 'Permission Created',
        message: 'Permission created successfully.'
      },
      UPDATED: {
        title: 'Permission Updated',
        message: 'Permission updated successfully.'
      },
      DELETE_RESTRICTED: {
        title: 'Action Restricted',
        message: 'System permissions and the LIST permission cannot be deleted.'
      },
      DELETE_CONFIRM: {
        title: 'Delete Permission',
        message: 'This permission will be permanently deleted. This action cannot be undone.',
        expectedText: 'DELETE',
        confirmText: 'Yes, I Confirm',
        cancelText: 'Cancel'
      },
      DELETED: {

        title: 'Permission Deleted',
        message: 'Permission deleted successfully.'
      },
      DELETE_FAILED: {
        title: 'Unable to Delete Permission',
        message: 'Failed to delete permission.'
      }
    }
  },

  JOB_ASSIGNMENT: {
    VALIDATION: {
      JOB_ID_MISSING: {
        title: 'Unable to Process Request',
        message: 'Job id is missing.'
      },
      SELECT_MANAGER: {
        title: 'Please Check the Form',
        message: 'Select at least one manager user.'
      },
      SELECT_BRANCH: {
        title: 'Please Check the Form',
        message: 'Select one of your assigned branches.'
      },
      BRANCH_NOT_OWNED: {
        title: 'Action Restricted',
        message: 'You can delegate only a branch assigned directly to you.'
      },
      RECRUITER_PERMISSION_DENIED: {
        title: 'Access Restricted',
        message: 'You do not have permission to assign recruiter ownership for this job.'
      },
      RECRUITER_BRANCH_NOT_OWNED: {
        title: 'Action Restricted',
        message: 'You can assign recruiters only for a branch assigned directly to you.'
      },
      SELECT_PRIMARY_RECRUITER: {
        title: 'Please Check the Form',
        message: 'Select at least one primary recruiter.'
      },
      RELEASE_REASON_REQUIRED: {
        title: 'Please Check the Form',
        message: 'Release reason is required.'
      },
      RELEASE_TARGET_MISSING: {
        title: 'Please Check the Form',
        message: 'Select an assignment to release.'
      },
      RELEASE_ASSIGNMENT_NOT_ALLOWED: {
        title: 'Access Restricted',
        message: 'You are not allowed to release this recruiter assignment.'
      },
      RELEASE_BRANCH_NOT_ALLOWED: {
        title: 'Access Restricted',
        message: 'You are not allowed to release this assignment branch.'
      }
    },

    MANAGERS: {
      ASSIGN_SUCCESS: {
        title: 'Job Assigned',
        message: 'The job was assigned to the selected management users.'
      },
      DELEGATE_SUCCESS: {
        title: 'Branch Delegated',
        message: 'The selected branch was delegated successfully.'
      },
      ASSIGN_FAILED: {
        title: 'Unable to Assign Management',
        message: "We're having a temporary issue assigning the selected management users. Please try again in a moment."
      }
    },

    RECRUITERS: {
      SAVE_SUCCESS: {
        title: 'Recruiter Ownership Updated',
        message: 'Recruiter ownership was updated successfully.'
      },
      SAVE_FAILED: {
        title: 'Unable to Update Recruiter Ownership',
        message: "We're having a temporary issue updating recruiter ownership. Please try again in a moment."
      }
    },

    RELEASE: {
      ASSIGNMENT_SUCCESS: {
        title: 'Assignment Released',
        message: 'Recruiter assignment was released.'
      },
      ASSIGNMENT_FAILED: {
        title: 'Unable to Release Assignment',
        message: "We're having a temporary issue releasing the recruiter assignment. Please try again in a moment."
      },
      BRANCH_SUCCESS: {
        title: 'Branch Released',
        message: 'Assignment branch was released.'
      },
      BRANCH_FAILED: {
        title: 'Unable to Release Branch',
        message: "We're having a temporary issue releasing the assignment branch. Please try again in a moment."
      }
    },

    WORKSPACE: {
      LOAD_FAILED: {
        title: 'Unable to Load Assignment Information',
        message: "We're having a temporary issue loading job-assignment information. Please try again in a moment."
      }
    }
  }

} as const;

export type ApiResponseType =
  | 'SUCCESS'
  | 'ERROR'
  | 'WARNING'
  | 'INFO'
  | 'VALIDATION'
  | 'CONFIRMATION';

export interface ApiFeedback extends AppMessage {
  type: ApiResponseType;
  status: number;
  success: boolean;
}

function extractValidationMessages(payload: any): string[] {
  const sources = [
    payload?.validationErrors,
    payload?.errors,
    payload?.fieldErrors,
    payload?.data?.validationErrors,
    payload?.data?.errors,
    payload?.data?.fieldErrors
  ];

  const messages: string[] = [];

  for (const source of sources) {
    if (!source) continue;

    if (Array.isArray(source)) {
      source.forEach(item => {
        const message =
          typeof item === 'string'
            ? item
            : item?.message || item?.defaultMessage || item?.detail;

        if (typeof message === 'string' && message.trim()) {
          messages.push(message.trim());
        }
      });

      continue;
    }

    if (typeof source === 'object') {
      Object.entries(source).forEach(([field, value]) => {
        if (typeof value === 'string' && value.trim()) {
          messages.push(`${formatFieldName(field)}: ${value.trim()}`);
          return;
        }

        if (!Array.isArray(value)) return;

        value.forEach(item => {
          const message =
            typeof item === 'string'
              ? item
              : (item as any)?.message ||
                (item as any)?.defaultMessage ||
                (item as any)?.detail;

          if (typeof message === 'string' && message.trim()) {
            messages.push(`${formatFieldName(field)}: ${message.trim()}`);
          }
        });
      });
    }
  }

  return Array.from(new Set(messages));
}

function formatFieldName(field: string): string {
  return String(field || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, value => value.toUpperCase());
}

function firstString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
}

function toNumber(...values: unknown[]): number {
  for (const value of values) {
    const numberValue = Number(value);

    if (Number.isFinite(numberValue)) {
      return numberValue;
    }
  }

  return 0;
}

function unwrapApiPayload(source: any): any {
  return source?.error ?? source ?? {};
}

function normalizeApiResponseType(value: unknown): ApiResponseType | null {
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toUpperCase();

  switch (normalized) {
    case 'SUCCESS':
    case 'ERROR':
    case 'WARNING':
    case 'INFO':
    case 'VALIDATION':
    case 'CONFIRMATION':
      return normalized;

    default:
      return null;
  }
}

function inferApiResponseType(
  status: number,
  success: unknown,
  fallbackType: ApiResponseType
): ApiResponseType {
  if (status === 400 || status === 422) {
    return 'VALIDATION';
  }

  if (status === 409 || status === 429) {
    return 'WARNING';
  }

  if (status >= 400 || success === false) {
    return 'ERROR';
  }

  if ((status >= 200 && status < 300) || success === true) {
    return 'SUCCESS';
  }

  return fallbackType;
}

function statusFallbackMessage(status: number, fallback: string): string {
  switch (status) {
    case 0:
      return APP_MESSAGES.COMMON.ERRORS.NETWORK;

    case 401:
      return APP_MESSAGES.COMMON.ERRORS.UNAUTHORIZED;

    case 403:
      return APP_MESSAGES.COMMON.ERRORS.FORBIDDEN;

    case 404:
      return APP_MESSAGES.COMMON.ERRORS.NOT_FOUND;

    case 500:
    case 502:
    case 503:
    case 504:
      return APP_MESSAGES.COMMON.ERRORS.SERVER;

    default:
      return fallback;
  }
}

export function resolveApiFeedback(
  source: any,
  fallback: AppMessage,
  fallbackType: ApiResponseType
): ApiFeedback {
  const payload = unwrapApiPayload(source);

  const status = toNumber(
    payload?.status,
    source?.status
  );

  const success =
    typeof payload?.success === 'boolean'
      ? payload.success
      : status >= 200 && status < 300;

  const type =
    normalizeApiResponseType(payload?.type) ||
    inferApiResponseType(status, payload?.success, fallbackType);

  const title = firstString(
    payload?.title,
    payload?.data?.title,
    fallback.title
  );

  const validationMessages = extractValidationMessages(payload);

  const backendMessage = firstString(
    typeof payload === 'string' ? payload : '',
    payload?.message,
    payload?.detail,
    payload?.error?.message,
    payload?.data?.message,
    payload?.data?.detail,
    typeof payload?.error === 'string' ? payload.error : ''
  );

  let message = '';

  if (backendMessage && validationMessages.length) {
    message = Array.from(
      new Set([backendMessage, ...validationMessages])
    ).join('\n');
  } else if (validationMessages.length) {
    message = validationMessages.join('\n');
  } else if (backendMessage) {
    message = backendMessage;
  } else {
    message = statusFallbackMessage(
      status,
      firstString(source?.message, fallback.message)
    );
  }

  return {
    type,
    status,
    success,
    title: title || fallback.title,
    message: message || fallback.message
  };
}

/**
 * Kept for existing components that only need an error message.
 */
export function resolveApiError(
  error: any,
  fallback: string = APP_MESSAGES.COMMON.ERRORS.DEFAULT
): string {
  return resolveApiFeedback(
    error,
    {
      title: APP_MESSAGES.COMMON.TITLES.ERROR,
      message: fallback
    },
    'ERROR'
  ).message;
}

/**
 * Kept for existing components that only need a success message.
 */
export function resolveApiSuccess(
  response: any,
  fallback: string
): string {
  return resolveApiFeedback(
    response,
    {
      title: APP_MESSAGES.COMMON.TITLES.SUCCESS,
      message: fallback
    },
    'SUCCESS'
  ).message;
}